import React from 'react'
import { cn } from './cn'

/**
 * `web`     → 行銷／web app 頁首（.page-head ＋ .eyebrow / .lead）。
 * `column`  → App 面的欄標題（design-guideline §3.5）：襯線 500 的固定頁面大標，右邊一組
 *             圖示鈕（`IconButton variant="bar"`）。只有標題與工具，不吃 eyebrow、subtitle。
 */
export type PageHeaderVariant = 'web' | 'column'

export interface PageHeaderProps {
  title: React.ReactNode
  /** 標題標籤。一律 render 真正的 h1/h2 —— 中文字體 subset 掃描依賴標題標籤，勿改成 div */
  as?: 'h1' | 'h2'
  /** mono 大字距 eyebrow（.eyebrow）。僅 web variant。 */
  eyebrow?: React.ReactNode
  /** 副標（.lead）。僅 web variant。 */
  subtitle?: React.ReactNode
  /** 右上動作（如按鈕）。有 actions 時標題區包進 <div>、actions 為其同層 sibling，對齊現有頁面結構 */
  actions?: React.ReactNode
  className?: string
  /** 頁首樣式分流。預設 web，採用 Open Design 的 app title scale。 */
  variant?: PageHeaderVariant
}

/**
 * Open Design `app-team.html` / `transcript-list.html` 的 app page title。
 * PageHeader 與內聯的團隊名稱共用，避免同一個 app shell 出現兩種 title 尺度。
 */
export interface ScreenTitleProps extends React.HTMLAttributes<HTMLHeadingElement> {
  as?: 'h1' | 'h2'
}

/**
 * The app page-title type scale, as a bare class string. Exported so an editable
 * title (`<InlineEdit as="h1">` on the team page) can wear the SAME scale as
 * `ScreenTitle` without copying the utilities into a second place.
 */
export const screenTitleClassName =
  'font-display text-[length:var(--fs-app-page-title)] font-semibold leading-[1.2] tracking-[0.005em]'

export function ScreenTitle({ as = 'h1', className, children, ...props }: ScreenTitleProps) {
  const Heading = as
  return (
    <Heading
      {...props}
      data-ui="screen-title"
      className={cn(screenTitleClassName, className)}
    >
      {children}
    </Heading>
  )
}

/**
 * Open Design app page lead. It always belongs directly under ScreenTitle,
 * regardless of whether the page also has right-aligned actions.
 */
export type PageDescriptionProps = React.HTMLAttributes<HTMLParagraphElement>

export function PageDescription({ className, children, ...props }: PageDescriptionProps) {
  return (
    <p
      {...props}
      data-ui="screen-description"
      className={cn('mt-[var(--gap-xs)] max-w-[44em] text-md leading-[1.65] text-muted', className)}
    >
      {children}
    </p>
  )
}

/**
 * 頁首：web 頁面的 `.page-head`（Open Design 的 app title scale），與桌面 App 面的欄標題
 * （`column`）。
 */
export function PageHeader({
  title,
  as = 'h1',
  eyebrow,
  subtitle,
  actions,
  className,
  variant = 'web',
}: PageHeaderProps) {
  if (variant === 'column') {
    const Heading = as
    return (
      <header
        data-page-header={variant}
        // The right inset is one gutter less the icon button's own 10px air, so
        // the last icon's glyph lines up with the content's right edge.
        className={cn(
          'flex min-h-[var(--d-bar-h)] shrink-0 items-center justify-between gap-2 pl-[var(--m-gutter)] pr-[calc(var(--m-gutter)-var(--gap-compact))] pt-[var(--d-bar-top)]',
          className
        )}
      >
        <Heading className="m-0 min-w-0 truncate font-display text-[length:var(--fs-m-page)] font-medium leading-[1.25] [font-variant-numeric:lining-nums]">
          {title}
        </Heading>
        {actions != null ? <div className="flex shrink-0 items-center">{actions}</div> : null}
      </header>
    )
  }

  const titleBlock = (
    <div data-ui="page-header-content">
      {eyebrow != null ? <p className="mb-1 font-mono text-2xs uppercase tracking-[var(--tracking-label)] text-muted">{eyebrow}</p> : null}
      <ScreenTitle as={as}>{title}</ScreenTitle>
      {subtitle != null ? <PageDescription>{subtitle}</PageDescription> : null}
    </div>
  )
  return (
    <header data-page-header={variant} className={cn('page-head mb-[var(--gap-lg)] flex items-start justify-between gap-4', className)}>
      {titleBlock}
      {actions}
    </header>
  )
}
